"use client";

import { useEffect, useState } from "react";

import LoginForm from "@/components/auth/login-form";

type LoginCms = {
  badge_text: string; hero_title: string; hero_subtitle: string;
  bullets: string[]; footer_line: string; disclaimer: string;
};

// Drawn from mymtree.com's own positioning rather than invented for this
// screen — the site sells structural discipline over prediction, and the
// sign-in page was still promising "a desk that thinks in seasons" with no
// explanation of what that means to somebody about to type a password.
//
// Every claim here is one the platform can actually back: the +5% harvest
// and −3% prune are the real thresholds, 21 personas and 5 pathways are
// counted, and the paper-desk line is the truth about every new account.
const FALLBACK: LoginCms = {
  badge_text: "Quantitative trading research & education",
  hero_title: "Inspired by nature. Engineered for resilience.",
  hero_subtitle:
    "A tree harvests in abundance, sheds what is dying, and keeps its roots "
    + "deep enough to survive a winter it cannot predict. myMTree applies "
    + "that discipline to your capital — as rules the system enforces, not "
    + "intentions you have to remember at the worst possible moment.",
  bullets: [
    "Harvest at +5%, prune at −3% — mechanically, not when you feel brave enough.",
    "Every order stops at a human. Nothing trades without you.",
    "21 personas competing in paper across 5 pathways, from saver to allocator.",
    "Your book is schema-isolated at the database level, not filtered in code.",
  ],
  footer_line: "Every account starts on a simulated paper desk.",
  disclaimer:
    "Research and education. Nothing here is investment advice, and no past "
    + "or projected performance is shown or implied.",
};

const Login = () => {
  const [cms, setCms] = useState<LoginCms>(FALLBACK);

  useEffect(() => {
    // public endpoint — the sign-in page has no session by definition
    fetch("/api/v5/public/content/auth_login")
      .then((r) => (r.ok ? r.json() : null))
      .then((d: Partial<LoginCms> | null) => {
        if (!d) return;
        // merge field by field: an edit that clears one line must not blank
        // the whole panel
        setCms((c) => ({
          badge_text: d.badge_text || c.badge_text,
          hero_title: d.hero_title || c.hero_title,
          hero_subtitle: d.hero_subtitle || c.hero_subtitle,
          bullets: d.bullets?.length ? d.bullets : c.bullets,
          footer_line: d.footer_line || c.footer_line,
          disclaimer: d.disclaimer || c.disclaimer,
        }));
      })
      .catch(() => undefined);
  }, []);

  return (
    <section className="bg-white dark:bg-slate-900 flex flex-wrap min-h-screen">
      {/* Left brand pane — forest gradient + value statements */}
      <div className="lg:w-1/2 hidden lg:block">
        <div className="flex h-screen flex-col justify-between bg-gradient-to-br from-[#052e1c] via-[#064e3b] to-[#0f172a] p-12 text-white">
          <div className="flex items-center gap-3">
            <span className="text-4xl">🌳</span>
            <span className="text-3xl font-bold tracking-tight">
              myM<span className="text-[#34d399]">Tree</span>
            </span>
          </div>
          {/* Copy comes from the CMS with these as the fallback, so the
              sign-in page can be edited in Content Studio like every other
              surface. The fallback is the SHIPPED copy rather than a
              placeholder: a login screen that renders blank because a row is
              missing is worse than one that reads slightly stale. */}
          <div className="max-w-md">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-emerald-300/70">
              {cms.badge_text}
            </p>
            <h2 className="text-4xl font-bold leading-tight">
              {cms.hero_title}
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-emerald-100/80">
              {cms.hero_subtitle}
            </p>
            <ul className="mt-8 space-y-4 text-base text-emerald-100/90">
              {cms.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="text-[#34d399]">✓</span> {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="text-sm text-emerald-200/60">
            <p className="mb-1 font-semibold text-emerald-200/80">
              {cms.footer_line}
            </p>
            <p>{cms.disclaimer}</p>
          </div>
        </div>
      </div>

      {/* Right Form */}
      <div className="lg:w-1/2 w-full py-8 px-6 flex flex-col justify-center">
        <div className="lg:max-w-[464px] w-full mx-auto">
          {/* Logo and heading */}
          <div>
            <div className="mb-6 flex items-center gap-3">
              <span className="text-3xl">🌳</span>
              <span className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                myM<span className="text-[#10b981]">Tree</span>
              </span>
            </div>

            <h4 className="font-semibold mb-3">Sign In to your Account</h4>
            <p className="mb-8 text-neutral-500 dark:text-neutral-300 text-lg">
              Welcome back! Please enter your details.
            </p>
          </div>

          {/* Login Form */}
          <LoginForm />
        </div>
      </div>
    </section>
  );
};

export default Login;
